import React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import SaveIcon from "@mui/icons-material/Save";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_DESTRUCTIVE,
  COLOR_DESTRUCTIVE_HOVER,
  COLOR_GREY_300,
} from "../../styles/colors";
import IOPSConfigModal from "../game/IOPSConfigModal";
import SaveDesignModal from "./SaveDesignModal";
import MyDesignsModal from "./MyDesignsModal";

interface ScoreBoardActionsProps {
  isMobile: boolean;
  isRunning: boolean;
  onSettingsClick: () => void;
  onPlayPauseClick: () => void;
  onResetConfirm: () => void;
  onDesignSaved: (name: string) => void;
  onDesignLoaded: (name: string) => void;
}

const ScoreBoardActions: React.FC<ScoreBoardActionsProps> = ({
  isMobile,
  isRunning,
  onSettingsClick,
  onPlayPauseClick,
  onResetConfirm,
  onDesignSaved,
  onDesignLoaded,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [isMyDesignsModalOpen, setIsMyDesignsModalOpen] = React.useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSettingsClick = () => {
    onSettingsClick();
    setIsSettingsModalOpen(true);
  };

  const btnSize = isMobile ? "28px" : "35px";
  const iconSize = isMobile ? "16px" : "24px";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isMobile ? "4px" : "8px",
          justifyContent: "space-evenly",
          flexGrow: 1,
        }}
      >
        <IconButton
          aria-label="settings"
          data-testid="settings-btn"
          onClick={handleSettingsClick}
          sx={{
            backgroundColor: COLOR_PRIMARY,
            color: "white",
            width: btnSize,
            height: btnSize,
            "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
          }}
        >
          <SettingsIcon sx={{ fontSize: iconSize }} />
        </IconButton>

        <IconButton
          aria-label="play pause"
          data-testid="play-pause-btn"
          onClick={onPlayPauseClick}
          sx={{
            backgroundColor: COLOR_PRIMARY,
            color: COLOR_PRIMARY,
            width: btnSize,
            height: btnSize,
            "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
          }}
        >
          {isRunning ? (
            <PauseIcon sx={{ color: "white", fontSize: iconSize }} />
          ) : (
            <PlayArrowIcon sx={{ color: "white", fontSize: iconSize }} />
          )}
        </IconButton>

        <IconButton
          aria-label="save design"
          data-testid="save-design-btn"
          onClick={() => setIsSaveModalOpen(true)}
          sx={{
            backgroundColor: COLOR_PRIMARY,
            color: "white",
            width: btnSize,
            height: btnSize,
            "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
          }}
        >
          <SaveIcon sx={{ fontSize: iconSize }} />
        </IconButton>

        <IconButton
          aria-label="reset"
          data-testid="reset-btn"
          onClick={() => setIsResetConfirmOpen(true)}
          sx={{
            backgroundColor: COLOR_DESTRUCTIVE,
            color: "white",
            width: btnSize,
            height: btnSize,
            "&:hover": { backgroundColor: COLOR_DESTRUCTIVE_HOVER },
          }}
        >
          <ReplayIcon sx={{ fontSize: iconSize }} />
        </IconButton>

        <IconButton
          aria-label="more options"
          data-testid="more-options-btn"
          onClick={handleMenuOpen}
          size="medium"
          sx={{
            color: COLOR_GREY_300,
            p: "4px",
            ml: "-4px",
            "&:hover": {
              color: COLOR_PRIMARY_DARK,
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          <MenuIcon fontSize="medium" />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setIsMyDesignsModalOpen(true);
            }}
          >
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Designs</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">{"Reset Canvas?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset the canvas? All your blocks and
            connections will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsResetConfirmOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsResetConfirmOpen(false);
              onResetConfirm();
            }}
            color="error"
            data-testid="reset-confirm-btn"
            autoFocus
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings / IOPS Config Modal */}
      <IOPSConfigModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      {/* Save Design Modal */}
      <SaveDesignModal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSaved={(name) => {
          onDesignSaved(name);
        }}
      />

      {/* My Designs Modal */}
      <MyDesignsModal
        open={isMyDesignsModalOpen}
        onClose={() => setIsMyDesignsModalOpen(false)}
        onDesignLoaded={(name) => {
          onDesignLoaded(name);
        }}
      />
    </>
  );
};

export default ScoreBoardActions;
